import { useState } from "react";
import { fmtDateTime } from "@/components/admin/format";
import { JsonDiff } from "@/components/admin/JsonDiff";
import { KIND_META, isMasterKind } from "@/components/admin/master-kinds";
import { LoadState, PageHead } from "@/components/admin/PageHead";
import { useLoad } from "@/components/admin/useLoad";
import { Button } from "@/components/ui/FormControls";

interface Log {
  id: string;
  at: string;
  by: string | null;
  action: string;
  kind: string | null;
  key: string | null;
  before: unknown;
  after: unknown;
}

const ACTION_LABEL: Record<string, string> = {
  "master.create": "추가",
  "master.update": "수정",
  "master.deactivate": "비활성화",
  "master.seed": "기본값 채우기",
  "master.reset": "기본값 되돌리기",
  "user.role": "역할 변경",
  "user.delete": "사용자 삭제",
};

/** 변경 이력 , 누가 언제 무엇을 어떻게 바꿨는지. 행을 펼치면 전후 JSON */
export function AuditPage() {
  const { data, error, loading, reload } = useLoad<{ logs: Log[] }>("/admin/audit?limit=200");
  const [open, setOpen] = useState<Set<string>>(new Set());

  function toggle(id: string) {
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  const logs = data?.logs ?? [];
  return (
    <div className="admin-page">
      <PageHead
        title="변경 이력"
        desc="마스터 데이터 편집과 사용자 관리 동작을 최근 200건까지 확인합니다. 산정로직 정의서와 검수 근거로 씁니다."
        aside={
          <Button variant="secondary" onClick={reload} disabled={loading}>
            새로 고침
          </Button>
        }
      />
      <LoadState loading={loading && !data} error={error} empty={!!data && logs.length === 0} emptyText="아직 기록이 없습니다." />
      {logs.length > 0 && (
        <div className="table-scroll admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>시각</th>
                <th>사용자</th>
                <th>동작</th>
                <th>종류</th>
                <th>키</th>
                <th>전후</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => {
                const expanded = open.has(l.id);
                const hasDiff = l.before !== null || l.after !== null;
                return [
                  <tr key={l.id}>
                    <td className="cell-nowrap font-mono">{fmtDateTime(l.at)}</td>
                    <td className="cell-key">{l.by ?? <span className="cell-muted">(삭제된 계정)</span>}</td>
                    <td className="cell-nowrap">{ACTION_LABEL[l.action] ?? l.action}</td>
                    <td className="cell-nowrap">{l.kind ? (isMasterKind(l.kind) ? KIND_META[l.kind].label : l.kind) : <span className="cell-muted">없음</span>}</td>
                    <td className="cell-key">{l.key ?? <span className="cell-muted">없음</span>}</td>
                    <td>
                      {hasDiff ? (
                        <Button size="compact" aria-expanded={expanded} onClick={() => toggle(l.id)}>
                          {expanded ? "접기" : "펼치기"}
                        </Button>
                      ) : (
                        <span className="cell-muted">없음</span>
                      )}
                    </td>
                  </tr>,
                  expanded ? (
                    <tr key={`${l.id}-diff`}>
                      <td colSpan={6}>
                        <JsonDiff before={l.before} after={l.after} />
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
